import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../../components/atoms/input-field';

import { useUpdateDestinationDialogValidationSchema } from './schema/update-destination-dialog-validation-schema';

import { Dialog, DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { useUpdateDestination } from '@/features/destinations/hooks/mutation/use-update-destination';

interface Props extends DialogActionsInterface {
  id: number;
  name: string;
  floor: string;
  description: string;
  maxCapacity: number;
  accessBeforeVisitInMinutes: number;
  accessAfterVisitInMinutes: number;
}

interface FormValues {
  name: string;
  floor: string;
  description: string;
  maxCapacity: number;
  accessBeforeVisitInMinutes: number;
  accessAfterVisitInMinutes: number;
}

export const UpdateDestinationDialog = ({
  id,
  name,
  floor,
  description,
  maxCapacity,
  accessBeforeVisitInMinutes,
  accessAfterVisitInMinutes,
  open,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['createDestination', 'destination', 'common']);

  const validationSchema = useUpdateDestinationDialogValidationSchema();

  const defaultValues: FormValues = {
    name,
    floor,
    description,
    maxCapacity,
    accessBeforeVisitInMinutes,
    accessAfterVisitInMinutes,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(defaultValues);
  }, [
    open,
    reset,
    name,
    floor,
    description,
    maxCapacity,
    accessBeforeVisitInMinutes,
    accessAfterVisitInMinutes,
  ]);

  const { mutate, isPending } = useUpdateDestination(onCancel);

  const onSubmit = (data: FormValues) => {
    mutate({ payload: data, destinationId: id });
  };

  return (
    <Dialog
      title={t('destination:updateDialogTitle')}
      onCancel={onCancel}
      open={open}
      confirmButtonText={t('common:button.edit')}
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}
      onConfirm={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.name')}
                required
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="floor"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.floor')}
                required
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.description')}
                required
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="maxCapacity"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.maxCap')}
                required
                type="number"
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="accessBeforeVisitInMinutes"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.accessBefore')}
                required
                type="number"
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="accessAfterVisitInMinutes"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:info.accessAfter')}
                required
                type="number"
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
